if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = null;
        this.productCard = this.closest('.product-card, .hindle-v2-product-card-wrap');
        this.productUrl = this.productCard?.querySelector(
          'a[id^="CardLink"], .hindle-v2-product-card__title-link, .hindle-v2-product-card__media-link, .hindle-v2-product-card__body-link'
        )?.href;
        this.preloadData = null;
        this.isV2QuickAdd = false;

        const cardLink = this.productCard?.querySelector(
          'a[id^="CardLink"], .hindle-v2-product-card__title-link, .hindle-v2-product-card__media-link, .hindle-v2-product-card__body-link'
        );
        const stickerText = cardLink?.querySelector('.sticker-text');
        const v2Title = this.productCard?.querySelector('.hindle-v2-product-card__title');
        this.productTitle =
          stickerText?.textContent || v2Title?.textContent?.trim() || cardLink?.textContent?.trim();
      }

      connectedCallback() {
        super.connectedCallback?.();
        this.ensureModalContent();
      }

      ensureModalContent() {
        if (!this.modalContent) {
          this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        }
        return this.modalContent;
      }

      resolveProductElement(doc) {
        const legacy = doc.querySelector('div[id^="MainProduct-"]');
        if (legacy) {
          this.isV2QuickAdd = false;
          return legacy;
        }

        const v2Section = doc.querySelector('section[id^="HindleV2Product-"]');
        if (!v2Section) return null;

        this.isV2QuickAdd = true;
        const fold = v2Section.querySelector('.hindle-v2-product__fold');
        const wrapper = doc.createElement('div');
        wrapper.className = 'quick-add-v2-product hindle-v2-scope hindle-v2-product';
        wrapper.dataset.section = v2Section.dataset.section;
        wrapper.innerHTML = fold ? fold.innerHTML : v2Section.innerHTML;
        return wrapper;
      }

      loadStylesheet(href, reparent = false) {
        if (!href) return Promise.resolve();

        return new Promise((resolve) => {
          const existing = document.querySelector(`link[href="${href}"]`);

          if (existing) {
            if (reparent) {
              document.head.appendChild(existing);
            }
            resolve();
            return;
          }

          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = () => resolve();
          link.onerror = () => resolve();
          document.head.appendChild(link);
        });
      }

      loadScript(src) {
        if (!src) return Promise.resolve();

        if (document.querySelector(`script[src="${src}"]`)) {
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          script.onload = () => resolve();
          script.onerror = () => resolve();
          document.head.appendChild(script);
        });
      }

      ensureV2Assets() {
        if (!this.isV2QuickAdd) return Promise.resolve();

        const modal = this.closest('quick-add-modal');
        const styleUrls = (modal?.dataset.v2Styles || '').split(',').filter(Boolean);
        const scriptUrls = (modal?.dataset.v2Scripts || '').split(',').filter(Boolean);
        const v2Css = modal?.dataset.v2Css || '';

        const styleLoads = styleUrls.map((url) => this.loadStylesheet(url));
        if (v2Css) {
          styleLoads.push(this.loadStylesheet(v2Css, true));
        }

        return Promise.all(styleLoads).then(() =>
          scriptUrls.reduce((chain, url) => chain.then(() => this.loadScript(url)), Promise.resolve())
        );
      }

      fetchProductData() {
        return fetch(this.productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = this.resolveProductElement(responseHTML);

            if (!this.productElement) {
              throw new Error('Product section not found');
            }

            const modalContent = this.ensureModalContent();
            if (modalContent) {
              modalContent.classList.toggle('quick-add-modal__content-info--v2', this.isV2QuickAdd);
            }

            if (!this.isV2QuickAdd) {
              this.productElement.classList.forEach((classApplied) => {
                if (classApplied.startsWith('color-') || classApplied === 'gradient') {
                  modalContent?.classList.add(classApplied);
                }
              });
            }

            this.preventDuplicatedIDs();
            this.removeDOMElements();
            return this.productElement.outerHTML;
          });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) {
          cartNotification.init();
          cartNotification.setActiveElement(this.openedBy);
        }

        const modalContent = this.ensureModalContent();
        if (modalContent) {
          modalContent.innerHTML = '';
          modalContent.classList.remove('quick-add-modal__content-info--v2');
        }
        this.isV2QuickAdd = false;

        if (preventFocus) this.openedBy = null;
        publish(PUB_SUB_EVENTS.quickViewClose);
        super.hide();
      }

      initV2ModalContent(modalContent) {
        modalContent.querySelectorAll('.hindle-v2-product-gallery').forEach((gallery) => {
          gallery.dataset.imageZoom = 'none';
          gallery.querySelectorAll('.product__media-toggle').forEach((toggle) => {
            toggle.setAttribute('tabindex', '-1');
            toggle.setAttribute('aria-hidden', 'true');
          });
        });

        modalContent.querySelectorAll('.hindle-v2-product-info__reviews').forEach((reviews) => {
          reviews.hidden = true;
        });

        if (typeof window.__hindleV2TrimJudgemeText === 'function') {
          window.__hindleV2TrimJudgemeText(modalContent);
        }

        if (typeof window.initHindleV2ProductGallery === 'function') {
          window.initHindleV2ProductGallery(modalContent);
        }
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden', 'spinning-complete');

        const finalizeShow = (productHTML) => {
          this.ensureV2Assets().then(() => {
            const modalContent = this.ensureModalContent();
            if (!modalContent) return;

            modalContent.classList.toggle('quick-add-modal__content-info--v2', this.isV2QuickAdd);
            this.setInnerHTML(modalContent, productHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();

            if (this.isV2QuickAdd) {
              this.initV2ModalContent(modalContent);
            } else {
              this.prependProductTitle();
            }

            window.renderSelects?.();
            window.loadDesktopOnlyTemplates?.();
            super.show(opener);

            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');

            publish(PUB_SUB_EVENTS.quickViewOpen);
            publish('quick-buy-action-complete');

            setTimeout(() => {
              if (window.ProductModel) window.ProductModel.loadShopifyXR();
            }, 500);
          });
        };

        if (this.preloadData) {
          finalizeShow(this.preloadData);
        } else {
          this.productUrl = opener.getAttribute('data-product-url');
          this.fetchProductData().then(finalizeShow).catch(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden', 'spinning-complete');
          });
        }
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        element.querySelectorAll('.quick-add-hidden').forEach((elem) => {
          if (elem.closest('.hindle-v2-product-info__notify-panel, .hindle-v2-product-info, product-info')) {
            elem.classList.remove('quick-add-hidden');
            return;
          }
          elem.remove();
        });
      }

      preventVariantURLSwitching() {
        const modalContent = this.ensureModalContent();
        if (!modalContent) return;

        modalContent.querySelectorAll('variant-selects, product-info').forEach((variantPicker) => {
          variantPicker.setAttribute('data-update-url', 'false');
        });
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        this.productElement.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        if (!sectionId) return;

        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const modalContent = this.ensureModalContent();
        if (!modalContent) return;

        const galleryList = modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const modalContent = this.ensureModalContent();
        if (!modalContent) return;

        const product = modalContent.querySelector('.product');
        if (!product) return;

        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      prependProductTitle() {
        if (this.isV2QuickAdd) return;

        const modalContent = this.ensureModalContent();
        if (!modalContent) return;

        const product = modalContent.querySelector('.product');
        if (!product || product.querySelector('.product__title')) return;

        const container = product.querySelector('product-info .product__info-container');
        if (!container) return;

        const readMore = window.strings?.product?.readMore || '';
        const productTitleHTML = `
              <div class="product__title">
                <a href="${this.productUrl}" class="link un-underlined-link">
                  <h2 class="h2 mt-0">
                    ${this.productTitle}
                    <span class="product__read-more block underlined-link t4">${readMore}</span>
                  </h2>
                </a>
              </div>`;
        container.innerHTML = productTitleHTML + container.innerHTML;
      }
    }
  );
}
